import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["message"]

  connect() {
    this.keepInView()

    // Auto-dismiss success messages after 5 seconds
    this.messageTargets.forEach(message => {
      const flashType = message.dataset.flashType
      if (flashType === 'notice' || flashType === 'success') {
        setTimeout(() => {
          this.dismissMessage(message)
        }, 5000)
      }
    })
  }

  disconnect() {
    this.observer?.disconnect()
    clearTimeout(this.settleTimer)
  }

  // Flash renders at the top of the layout, but a redirect can bring the page back
  // scrolled down — Turbo keeping the position after Clear Order, an anchor on the Pets
  // step — and then the message is above the fold where nobody reads it (B108). Watched
  // rather than measured once, because that scroll can land after this connects. The
  // first time the messages are out of sight they float under the header, and stay
  // there until they are dismissed: floating back into the page would jump it. Only
  // while the page arrives, though — a message the customer has seen and then scrolled
  // away from is not chased down the page.
  keepInView() {
    if (!("IntersectionObserver" in window)) return

    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) return

      this.observer.disconnect()
      this.float()
    }, { rootMargin: `-${this.headerBottom()}px 0px 0px 0px` })
    this.observer.observe(this.element)
    this.settleTimer = setTimeout(() => this.observer.disconnect(), 5000)
  }

  float() {
    this.element.style.setProperty("--flash-top", `${this.headerBottom() + 8}px`)
    this.element.dataset.floating = ""
  }

  // The site header is sticky, so a floating message sits under it rather than behind it.
  headerBottom() {
    const header = document.querySelector("nav.sticky")
    return header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0
  }

  dismiss(event) {
    const message = event.target.closest('[data-flash-target="message"]')
    this.dismissMessage(message)
  }

  dismissMessage(messageElement) {
    if (messageElement) {
      // Add fade out animation
      messageElement.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out'
      messageElement.style.opacity = '0'
      messageElement.style.transform = 'translateY(-10px)'
      
      // Remove element after animation
      setTimeout(() => {
        messageElement.remove()
        
        // Remove the entire flash container if no messages left
        const flashContainer = this.element
        if (flashContainer && flashContainer.querySelectorAll('[data-flash-target="message"]').length === 0) {
          flashContainer.remove()
        }
      }, 300)
    }
  }
}
